import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import WindowedSelect from 'react-windowed-select'

import TwoPartLabel from './option-renderers/TwoPartLabel'
import TwoPartOption from './option-renderers/TwoPartOption'

const StatelessSelectInput = props => {
  const {
    value,
    defaultValue,
    virtualized,
    className,
    hasError,
    onChange,
    onValueChange,
    getInputRef,
    multi,
    disabled,
    clearable,
    creatable,
    inputValue,
    options,
    ...otherProps
  } = props

  let SelectComponent = Select

  if (virtualized) {
    SelectComponent = WindowedSelect
  }

  if (creatable) {
    SelectComponent = CreatableSelect
  }

  if (!options) return null

  const selectedValue = multi
    ? options.filter(option => value ? value.includes(option) || value.includes(option.value) : null)
    : options.find(option => (option.value === value) || (option === value)) || null

  const selectedDefaultValue = multi
    ? options.filter(option => defaultValue ? defaultValue.includes(option) || defaultValue.includes(option.value) : null)
    : options.find(option => (option.value === defaultValue) || (option === defaultValue)) || null

  let selectProps = {
    components: {
      SingleValue: TwoPartLabel,
      Option: TwoPartOption
    },

    value: selectedValue,

    defaultValue: selectedDefaultValue,

    options: options,

    className: classnames(
      'vs-select',
      {
        'vs-select--has-error': hasError,
        'vs-form-field__select--has-error': hasError
      },
      'vs-form-field__select',
      className
    ),

    classNamePrefix: 'vs-select',

    isMulti: multi,

    isDisabled: disabled,

    isClearable: clearable && !inputValue,

    onChange (selectedOption) {
      let nextValue = selectedOption

      if (Array.isArray(selectedOption)) {
        nextValue = selectedOption.map(
          option => option.value
        )
      } else if (selectedOption) {
        nextValue = selectedOption.value
      } else {
        nextValue = null
      }

      onChange(selectedOption)
      onValueChange(nextValue)
    },

    ref: getInputRef,

    inputValue: inputValue,

    ...otherProps
  }

  if (virtualized) {
    selectProps = {
      ...selectProps,
      windowThreshold: 25
    }
  }

  return <SelectComponent {...selectProps} />
}

StatelessSelectInput.displayName = 'SelectInput.Stateless'

const SelectInput = ({
  value,
  defaultValue,
  onChange,
  onValueChange,
  ...otherProps
}) => {
  console.log(value)
  const [valueState, setValueState] = useState(
    defaultValue || value || null
  )
  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    setValueState(defaultValue || value || null)
  }, [value, defaultValue])

  const handleValueChange = (value) => {
    onValueChange(value)
  }

  const handleChange = (selectedOption) => {
    console.log(selectedOption)
    if (selectedOption !== valueState) {
      setValueState(selectedOption)

      onChange(selectedOption)
    }
  }

  const handleInputChange = (inputValue) => {
    setInputValue(inputValue)
  }

  return (
    <StatelessSelectInput
      value={valueState}
      inputValue={inputValue}
      onChange={handleChange}
      onValueChange={handleValueChange}
      onInputChange={handleInputChange}
      {...otherProps}
    />
  )
}

SelectInput.propTypes = {
  /**
   * This will not be passed on the field because of a bug in react-select
   */
  name: PropTypes.string,
  /**
   * The default input value (one of the options or the value of one)
   */
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
    PropTypes.array
  ]),
  /**
   * The input value (one of the options or the value of one).
   * Used as defaultValue
   */
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
    PropTypes.array
  ]),

  /** Whether or not the Select should have option to be cleared after value is there. */
  clearable: PropTypes.bool,
  /**
   * Determines if user can choose more options than one
   */
  multi: PropTypes.bool,
  /**
   * Uses https://github.com/bvaughn/react-virtualized-select/ for high numbers of options
   */
  virtualized: PropTypes.bool,
  /**
   * Classname(s) to pass to the form field
   */
  className: PropTypes.string,
  /**
   * Determines if presentation indicates an issue with the field
   */
  hasError: PropTypes.bool,
  /**
   * use to ref the react-select component
   */
  getInputRef: PropTypes.func,
  /**
   * Optional additional onChange handler (called after form onChange handler)
   */
  onChange: PropTypes.func,
  /**
   * Optional additional onValueChange handler (called after form onChange handler)
   * this is passing through the `react-select`'s value
   */
  onValueChange: PropTypes.func
}

SelectInput.defaultProps = {
  clearable: true,
  virtualized: false,
  multi: false,
  hasError: false,
  onChange: () => {},
  onValueChange: () => {},
  getInputRef: () => null
}

StatelessSelectInput.propTypes = SelectInput.propTypes
StatelessSelectInput.defaultProps = SelectInput.defaultProps

SelectInput.Stateless = StatelessSelectInput

export default SelectInput
export { TwoPartLabel }
